import {
  INDICATOR_REPORTING_ASSIGNMENT_ACCESS,
  INDICATOR_REPORTING_AUDIT_ACCESS,
  INDICATOR_REPORTING_FACILITY_WORKBENCH_ACCESS,
  INDICATOR_REPORTING_MODULE_ACCESS,
  INDICATOR_REPORTING_MONITORING_ACCESS,
  INDICATOR_REPORTING_REGISTRY_ACCESS,
  INDICATOR_REPORTING_TRACKING_ACCESS,
  deriveIndicatorReportingPermissionCodes,
} from "./indicator-reporting-permissions";

export interface IndicatorReportingNavigationItem {
  key: string;
  label: string;
  icon: string;
  route: string;
  description: string;
  permissions: string[];
  exact?: boolean;
  activePrefixes?: string[];
}

export interface IndicatorReportingNavigationPermissionContext {
  permissions?: readonly string[];
  roles?: readonly string[];
  isSystemAdmin?: boolean;
}

/**
 * Canonical, task-first navigation for the Indicator Reporting module.
 * Permissions keep each role's choice set small while preserving one shared
 * order across the module navigation, sidebar, and launchpad.
 */
export const INDICATOR_REPORTING_NAVIGATION_ITEMS: ReadonlyArray<IndicatorReportingNavigationItem> = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: "dashboard",
    route: "/indicator-reporting/dashboard",
    description: "Review reporting coverage, indicator performance, and priorities for your scope.",
    permissions: INDICATOR_REPORTING_MONITORING_ACCESS,
    exact: true,
  },
  {
    key: "tasks",
    label: "My Tasks",
    icon: "task_alt",
    route: "/indicator-reporting",
    description: "See reporting work that needs your attention.",
    permissions: INDICATOR_REPORTING_MODULE_ACCESS,
    exact: true,
  },
  {
    key: "facility-report",
    label: "Facility Report",
    icon: "assignment_turned_in",
    route: "/indicator-reporting/facility-workbench",
    description: "Capture, correct, save, and submit facility reports.",
    permissions: INDICATOR_REPORTING_FACILITY_WORKBENCH_ACCESS,
    activePrefixes: [
      "/indicator-reporting/facility-workbench",
      "/indicator-reporting/report-indicator",
    ],
  },
  {
    key: "submission-tracking",
    label: "Submission Tracking",
    icon: "fact_check",
    route: "/indicator-reporting/submission-tracking",
    description: "Review submissions and follow reporting coverage.",
    permissions: INDICATOR_REPORTING_TRACKING_ACCESS,
    activePrefixes: [
      "/indicator-reporting/submission-tracking",
      "/indicator-reporting/reviewer-queue",
      "/indicator-reporting/submission-monitoring",
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    icon: "insights",
    route: "/indicator-reporting/analytics",
    description: "View summary, aggregation, indicator detail, and data quality.",
    permissions: INDICATOR_REPORTING_MONITORING_ACCESS,
    activePrefixes: [
      "/indicator-reporting/analytics",
      "/indicator-reporting/summary",
    ],
  },
  {
    key: "indicator-management",
    label: "Indicator Management",
    icon: "rule",
    route: "/indicator-reporting/define-indicator",
    description: "Set up indicators, collection fields, and assignments.",
    permissions: INDICATOR_REPORTING_REGISTRY_ACCESS,
    activePrefixes: [
      "/indicator-reporting/define-indicator",
      "/indicator-reporting/registry/thematic-areas",
      "/indicator-reporting/registry/indicators",
      "/indicator-reporting/registry/surveys",
      "/indicator-reporting/registry/validation-rules",
      "/indicator-reporting/registry/cross-validation-rules",
    ],
  },
  {
    key: "facility-assignments",
    label: "Reporting Levels",
    icon: "assignment_ind",
    route: "/indicator-reporting/facility-assignments",
    description: "Choose the facility levels allowed to report each indicator.",
    permissions: INDICATOR_REPORTING_ASSIGNMENT_ACCESS,
    activePrefixes: ["/indicator-reporting/facility-assignments"],
  },
  {
    key: "tracer-medicines",
    label: "Tracer Medicines",
    icon: "medication",
    route: "/indicator-reporting/registry/tracer-medicines",
    description: "Configure the approved products used by SC6.",
    permissions: INDICATOR_REPORTING_REGISTRY_ACCESS,
  },
  {
    key: "version-editor",
    label: "Version Editor",
    icon: "history",
    route: "/indicator-reporting/registry/indicator-versions",
    description: "Manage indicator versions and their collection contracts.",
    permissions: INDICATOR_REPORTING_REGISTRY_ACCESS,
  },
  {
    key: "reporting-calendar",
    label: "Reporting Calendar",
    icon: "event_available",
    route: "/indicator-reporting/registry/reporting-calendar",
    description: "Set the recurring dates for each reporting frequency.",
    permissions: INDICATOR_REPORTING_REGISTRY_ACCESS,
  },
  {
    key: "audit",
    label: "Audit",
    icon: "manage_search",
    route: "/indicator-reporting/audit",
    description: "See who changed reporting information and when.",
    permissions: INDICATOR_REPORTING_AUDIT_ACCESS,
    activePrefixes: [
      "/indicator-reporting/audit",
      "/indicator-reporting/audits",
    ],
  },
];

export function getVisibleIndicatorReportingNavigationItems(
  context: IndicatorReportingNavigationPermissionContext,
): IndicatorReportingNavigationItem[] {
  if (context.isSystemAdmin) {
    return [...INDICATOR_REPORTING_NAVIGATION_ITEMS];
  }

  const roles = new Set(
    (context.roles ?? []).map(normalizeRoleValue).filter(Boolean),
  );
  const isHigherLevelUser = [
    "countrylevel",
    "regionlevel",
    "zonelevel",
    "woredalevel",
    "hublevel",
  ].some((role) => roles.has(role));

  const granted = new Set(
    [
      ...(context.permissions ?? []),
      ...(context.roles ?? []),
      ...deriveIndicatorReportingPermissionCodes([...(context.roles ?? [])]),
    ]
      .map(normalizeAccessValue)
      .filter(Boolean),
  );

  return INDICATOR_REPORTING_NAVIGATION_ITEMS.filter((item) => {
    if (item.key === "facility-report" && isHigherLevelUser) {
      return false;
    }
    return item.permissions.some((permission) =>
      granted.has(normalizeAccessValue(permission)),
    );
  });
}

export function isIndicatorReportingNavigationItemActive(
  item: IndicatorReportingNavigationItem,
  url: string | null | undefined,
): boolean {
  const currentPath = String(url || "").split(/[?#]/, 1)[0] || "/";
  if (item.exact) {
    return currentPath === item.route;
  }

  return (item.activePrefixes || [item.route]).some(
    (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`),
  );
}

function normalizeAccessValue(value: unknown): string {
  return String(value || "").trim().toLowerCase();
}

function normalizeRoleValue(value: unknown): string {
  return normalizeAccessValue(value).replace(/[\s_-]+/g, "");
}
